import { CurrencyCode } from '@99designs/i18n';
import { CategoryProductSchemaProps } from '@99designs/marketing-ui';
import { getCtaColorConfig } from '../util/getCtaColorConfig';
import { Price } from '../util/price';
import type { ButtonColorConfig } from '@99designs/emu';
import type { Site } from '@99designs/site-navigation/lib/Site';

export const vpProductLandingGroupKeys = ['business-card-design'] as const;
export const vpSDPMpvId = ['premiumbusinesscarddesign'] as const;

export type VPProductLandingGroupKey = typeof vpProductLandingGroupKeys[number];
export type VPSDPMpvId = typeof vpSDPMpvId[number];

type HeroImage = {
    sources: string[];
    altText: string;
    attributions?: {
        id: string;
        name: string;
    };
};

export type VPProductGroup = {
    title: string;
    description: string;
    heroTitle: string;
    productSchema?: CategoryProductSchemaProps;
    vpSDPLink: `/design/get-started?mpvId=${VPSDPMpvId}`;
    introduction: string;
    features: string[];
    heroImage: HeroImage;
    heroCtaAppearance?: ButtonColorConfig;
    whyVPTitle: string;
    whyVPImage: string;
    prices: Record<string, Price>;
};

export const getVPProductLandingGroups = (
    vpProductLandingGroupKey: VPProductLandingGroupKey,
    site: Site
): VPProductGroup => {
    const vpProductLandingGroups: Readonly<Record<VPProductLandingGroupKey, VPProductGroup>> = {
        'business-card-design': {
            title: 'Custom Business Card Design Services.',
            description:
                'Get a professional business card design from the creative experts. Original designs ready to print onto marketing materials, merch packaging and more. Award-winning designers. Real human customer service. Quality guaranteed.',
            heroTitle: 'Your business cards, made better.',
            productSchema: {
                categoryKey: 'business-card-design',
                productName: 'Business Card Design',
                description:
                    "Welcome to 99designs, the #1 trusted source for quality design you'll love. Get your perfect business card design with our expert designers today.",
            },
            vpSDPLink: '/design/get-started?mpvId=premiumbusinesscarddesign',
            heroCtaAppearance: getCtaColorConfig('#1C1B1A', '#5B5A58', '#6E6D6C', '#FFFFFF'),
            introduction:
                'With 99designs and VistaPrint working as one, you’ll get creative expertise, professional quality and print-ready designs – all wrapped into a single, seamless experience.',
            features: [
                '1 design concept based on your brief',
                '3-day turnaround time (excludes weekends and public holidays)',
                '5 revisions for you to use within 30 days to perfect your design',
                'Choose your designer to create a completely custom design',
                'Design delivered to your VistaPrint Account ready to print',
            ],
            heroImage: {
                sources: [
                    '/images/vp-product-landing/hero/business-card/business-card-hero_0-599.webp',
                    '/images/vp-product-landing/hero/business-card/business-card-hero_600-899.webp',
                    '/images/vp-product-landing/hero/business-card/business-card-hero_900-1299.webp',
                    '/images/vp-product-landing/hero/business-card/business-card-hero_1300-above.webp',
                ],
                altText: '', // Display as presentational
            },
            whyVPTitle: 'So, why VistaPrint for a business card design',
            whyVPImage: '',
            prices: {
                en_US: {
                    currency: site.get('currency') as CurrencyCode,
                    amountInCents: 3900,
                },
            },
        },
    };

    return vpProductLandingGroups[vpProductLandingGroupKey];
};
